import React, { useEffect } from 'react'
import PropTypes from 'prop-types'

import { FlaggedRouteContentGetter as RouteContentGetter } from 'layers/content/v2/Hocs/FlaggedRouteContentGetter'
import withApiError from 'layers/errorHandling/apiError'
import withSiteContext from 'layers/content/v2/Hocs/withSiteContext'
import { FlaggedCourseManagerGetter as CourseManagerGetter } from 'layers/content/v2/Hocs/FlaggedCourseManagerGetter'
import {
  isTier2PreviewMode,
  isDigitalWellBeingPreviewMode,
} from 'utils/urlHelpers'

import {
  selectCurrentClassInstanceData,
  parseGradeAndProgram,
} from 'utils/lmsHelpers'
import { resolveRouteServiceApp } from 'utils/productHelpers'
import { TICourseNameToProgramMap, K5_PROGRAM_TYPE } from 'utils/productMaps'

import {
  updateLessonCompleteness,
  findActiveHeader,
  prepareDataForHeaderTabs,
  prepareItemsForShutterList,
  generateProgressText,
  formatCourseDisplayTitle,
} from './data'
import {
  handleBackClick,
  handleHeaderTabChange,
  handleTabChange,
} from './helper'
import TieredInterventionsUnitsPage from './component'
import { PAGE_TITLE, BACK_LINK_ELEMENTARY, BACK_LINK_MIDDLE } from './constants'

const TieredInterventionsContainer = props => {
  const {
    match,
    location,
    navigationEvent,
    routeContent,
    instructorData,
    courseManager,
    userContext,
  } = props || {}
  const { url: currentUrl } = match || {}

  const { content = {}, loading } = routeContent || {}
  const { unitTi } = content || {}

  const { linkSummaries, tabSummaries, courseSummary } = unitTi || {}
  const { displayTitle } = courseSummary || {}

  let selectedClass = null
  let readyToLoad = false
  let updatedSummaries = linkSummaries || []
  let courseDisplayTitle = displayTitle
  // right now the only options are preview and teach
  const presentationMode = match?.params?.verb

  // we only get class instances in teach mode, and as a result, we only add class datat to summaries if in teach mode
  if (presentationMode === 'teach') {
    const { ourClass } = selectCurrentClassInstanceData({
      instructorData,
      courseManager,
      match,
      userContext,
      location,
    })
    updatedSummaries = updateLessonCompleteness({
      lessonSummaries: linkSummaries,
      classInstanceData: ourClass,
    })

    selectedClass = ourClass
    readyToLoad = !!selectedClass && !!content && !loading
    courseDisplayTitle = formatCourseDisplayTitle({
      displayTitle,
      selectedClass,
    })
  } else {
    readyToLoad = !!content && !loading
  }

  const activeHeaderTab = findActiveHeader(tabSummaries)
  const headerTabs = prepareDataForHeaderTabs({ tabSummaries, activeHeaderTab })

  // these are the main items shown on the page
  const items = prepareItemsForShutterList(updatedSummaries)

  const declareTabChange = handleTabChange({ location, navigationEvent })
  const declareHeaderTabChange = handleHeaderTabChange({
    tabSummaries,
    handleTabChange: declareTabChange,
  })

  const progressText = generateProgressText({
    currentUrl,
    ourClass: selectedClass,
  })

  // Standard page title setting
  const categoryTitle = activeHeaderTab?.title || PAGE_TITLE

  useEffect(() => {
    if (categoryTitle) {
      document.title = categoryTitle
    }
  }, [categoryTitle])

  const { grade: courseType } = parseGradeAndProgram(match?.url) || {}
  const readableProgramType = TICourseNameToProgramMap?.[courseType]

  const declareBackClick = handleBackClick({ navigationEvent, courseType })
  const backLinkText =
    readableProgramType === K5_PROGRAM_TYPE
      ? BACK_LINK_ELEMENTARY
      : BACK_LINK_MIDDLE
  const isPreview = isTier2PreviewMode() || isDigitalWellBeingPreviewMode()

  return (
    readyToLoad && (
      <TieredInterventionsUnitsPage
        activeHeaderTab={activeHeaderTab}
        backLinkText={backLinkText}
        content={content}
        courseDisplayTitle={courseDisplayTitle}
        handleBackClick={declareBackClick}
        handleHeaderTabChange={declareHeaderTabChange}
        handleTabChange={declareTabChange}
        headerTabs={headerTabs}
        isPreview={isPreview}
        items={items}
        linkSummaries={updatedSummaries}
        location={location}
        match={match}
        navigationEvent={navigationEvent}
        ourClass={selectedClass}
        presentationMode={presentationMode}
        programType={readableProgramType}
        progressText={progressText}
      />
    )
  )
}

TieredInterventionsContainer.propTypes = {
  courseManager: PropTypes.object,
  instructorData: PropTypes.object,
  location: PropTypes.object,
  match: PropTypes.object,
  navigationEvent: PropTypes.func,
  routeContent: PropTypes.object,
  userContext: PropTypes.object,
}

const options = {
  app: ({ route }) => resolveRouteServiceApp(route),
  routeResolver: () => window?.location?.pathname,
}

export default withSiteContext(
  CourseManagerGetter(
    RouteContentGetter(withApiError(TieredInterventionsContainer), options),
  ),
)
